import type { AgeBand } from '@disdis/shared'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { useChildStore } from '../state/store.js'
import { log, reason } from './log.js'

/**
 * The screenshot gate (CONVENTIONS §5) has to reach the child routes, and every one of them is
 * behind the `meta.activeChildId` guard (CONTRACTS §7) that only the parent onboarding can
 * satisfy. `?seed=mini` or `?seed=core` creates one fixed dev profile so a lane can shoot its
 * screens before WP-F's onboarding lands.
 *
 * Dev only: the whole body is behind `import.meta.env.DEV`, so the production bundle has no
 * path that writes a profile without the parent flow. It reuses the same fixed child id every
 * time, so running it twice adds nothing.
 */

const logger = log('dev-seed')

const DEV_CHILD_ID = '00000000-0000-4000-8000-0000000d15d1'
const BANDS: Record<string, AgeBand> = { mini: '5-7', core: '8-12' }

export function useDevSeed(): void {
  const [params] = useSearchParams()
  const childId = useChildStore((store) => store.childId)
  const status = useChildStore((store) => store.status)
  const seed = import.meta.env.DEV ? params.get('seed') : null

  useEffect(() => {
    if (!seed || childId || status !== 'ready') return
    const ageBand = BANDS[seed]
    if (!ageBand) return
    const run = async () => {
      const store = useChildStore.getState()
      await store.selectChild(DEV_CHILD_ID)
      await store.append('profile_created', {
        nickname: 'Deneme',
        ageBand,
        avatarId: 'avatar-1',
        petName: 'Pırıl',
      })
    }
    run().catch((error: unknown) => logger.warn('could not seed a dev profile', reason(error)))
  }, [seed, childId, status])
}
